import { useTranslation } from "react-i18next";
import useConfig from "@/features/config/hooks/useConfig.tsx";
import useTrackEvent from "@/features/widgets/hooks/useTrackEvent.tsx";
import PlaceHolder from "@/assets/icons/placeholder.svg?react";
import IconYes from "@/assets/icons/yes.svg?react";
import IconNo from "@/assets/icons/no.svg?react";
import uiHelper from "@/utils/uiHelper.tsx";
import { AnsweredQuestion, ResolvedProduct } from "@/features/widgets/components/Comparison/types/ComparisonTypes";

const CLASS_PREFIX = `ws-widgets-comparison-advice`;

type WinningProductProps = {
  product: ResolvedProduct;
  products: ResolvedProduct[];
  percentage: number | null;
  answeredQuestions: AnsweredQuestion[];
}

export const WinningProduct = ({ product, products, percentage, answeredQuestions }: WinningProductProps) => {
  const { t } = useTranslation();
  const config = useConfig();
  const trackEvent = useTrackEvent();

  const price = product.price ? uiHelper.formatPrice(product.price, config.language, config.currency) : null;
  const productId = product.id;
  const isMatch = (answeredQuestion: AnsweredQuestion): boolean =>
    answeredQuestion.productIds.includes(productId);

  const isBooleanSpec = (label: string): boolean =>
    products.some(p => p.attributes?.some(attribute => attribute.name === label && attribute.type === `boolean`));

  const displayValue = (value: string, label: string): string =>
    isBooleanSpec(label) ? t(value === `1` ? `comparison.advice.yes` : `comparison.advice.no`) : value;

  // Matches first, mismatches last.
  const sortedQuestions = [...answeredQuestions].sort((a, b) => Number(isMatch(b)) - Number(isMatch(a)));

  return (
    <div className={ `${ CLASS_PREFIX }__winner ${ CLASS_PREFIX }__winner--${ percentage }` }>
      <a className={ `${ CLASS_PREFIX }__winner-image` } href={ product.url } rel="noopener noreferrer">
        { product?.image ?
          <img src={ product.image } alt={ product.name }/>
          :
          <PlaceHolder/>
        }
      </a>
      <div className={ `${ CLASS_PREFIX }__winner-content` }>
        <div className={ `${ CLASS_PREFIX }__winner-title` }>
          <a href={ product.url } rel="noopener noreferrer">{ product.name }</a>
          <span className={ `${ CLASS_PREFIX }__winner-percentage` }>
            { percentage === 100 ? t(`comparison.advice.result_perfect_match`) : t(`comparison.advice.result_best_match`) }
          </span>
        </div>
        { answeredQuestions.length > 0 &&
          <div className={ `${ CLASS_PREFIX }__specs` }>
            <ul className={ `${ CLASS_PREFIX }__specs-list` }>
              { sortedQuestions.map((answeredQuestion, index) => {
                const matched = isMatch(answeredQuestion);
                // The product's actual value, for showing next to a struck-through mismatch.
                const actualOption = matched
                  ? undefined
                  : answeredQuestion.options.find(option => option.productIds.includes(productId));
                const specClasses = [
                  `${ CLASS_PREFIX }__spec`,
                  matched ? `${ CLASS_PREFIX }__spec--matched` : `${ CLASS_PREFIX }__spec--unmatched`,
                ].join(` `);

                return (
                  <li className={ specClasses } key={ index }>
                    <span className={ `${ CLASS_PREFIX }__spec-icon` }>
                      { matched ? <IconYes/> : <IconNo/> }
                    </span>
                    <span className={ `${ CLASS_PREFIX }__spec-text` }>
                      <strong>{ answeredQuestion.label }</strong>:{ ` ` }
                      { matched ?
                        displayValue(answeredQuestion.value, answeredQuestion.label)
                        :
                        <>
                          <s className={ `${ CLASS_PREFIX }__spec-struck` }>{ displayValue(answeredQuestion.value, answeredQuestion.label) }</s>
                          { actualOption && ` ${ displayValue(actualOption.value, answeredQuestion.label) }` }
                        </>
                      }
                    </span>
                  </li>
                );
              }) }
            </ul>
          </div>
        }
        <div className={ `${ CLASS_PREFIX }__winner-cta` }>
          { price &&
            <div className={ `${ CLASS_PREFIX }__winner-price` }>
              { price }
            </div>
          }
          <a
            className={ `ws-widgets-button ws-widgets-button--primary` }
            href={ product.url }
            onClick={ () => trackEvent(`click`, `comparison_buying_guide_buy`, product.name, products) }
          >
            { t(`core.button.buy`) }
          </a>
        </div>
      </div>
    </div>
  );
}
